import { z } from 'zod';
import { slugSchema } from './catalog.js';
import { designTargetShape } from '@imaji-studio/contract';

/**
 * Profil material dan kontur potong otomatis (KEPUTUSAN 3.6, 4.10).
 * Isi pengaturan kontur dan isian pelanggan divalidasi skema di
 * packages/geometry oleh API, karena paket shared tidak bergantung padanya.
 */

// ------------------------------------------------------------
// Profil material
// ------------------------------------------------------------

const materialFields = {
  slug: slugSchema,
  name: z.string().trim().min(2).max(80),
  thicknessMm: z.number().positive().max(100),
  kerfMm: z.number().min(0).max(1),
  minFeatureMm: z.number().positive().max(20),
  holeWallMm: z.number().positive().max(20),
  /** true = angka sudah diukur dari calibration kit (gerbang 3.3). */
  measured: z.boolean(),
  note: z.string().trim().max(500).nullable(),
};

export const materialProfileInputSchema = z.object({
  ...materialFields,
  measured: materialFields.measured.default(false),
  note: materialFields.note.default(null),
});
export type MaterialProfileInput = z.infer<typeof materialProfileInputSchema>;

export const materialProfileUpdateSchema = z
  .object(materialFields)
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'Tidak ada perubahan yang dikirim');
export type MaterialProfileUpdate = z.infer<typeof materialProfileUpdateSchema>;

export interface MaterialProfileDto {
  id: string;
  slug: string;
  name: string;
  thicknessMm: number;
  kerfMm: number;
  minFeatureMm: number;
  holeWallMm: number;
  measured: boolean;
  measuredAt: string | null;
  note: string | null;
  /** Template potong dan produk kontur yang memakai profil ini. */
  templateCount: number;
  contourCount: number;
  updatedAt: string;
}

// ------------------------------------------------------------
// Pengaturan kontur per produk
// ------------------------------------------------------------

export const productCutContourInputSchema = z.strictObject({
  enabled: z.boolean(),
  materialProfileId: z.string().min(1).max(64),
  /** Diperiksa `contourSettingsSchema` (packages/geometry) di API. */
  settings: z.record(z.string(), z.unknown()),
});
export type ProductCutContourInput = z.infer<typeof productCutContourInputSchema>;

/** Ukuran varian untuk kontur (sisi terpanjang), dibaca dari opsi produk. */
export interface ContourVariantSizeDto {
  label: string;
  longestMm: number;
}

export interface ProductCutContourAdminDto {
  /** false = belum pernah disimpan; pengaturan berisi bawaan. */
  saved: boolean;
  enabled: boolean;
  materialProfileId: string | null;
  settings: Record<string, unknown>;
  /** Alasan saklar belum bisa dinyalakan; kosong bila siap. */
  blockers: string[];
  variantSizes: ContourVariantSizeDto[];
  updatedAt: string | null;
}

// ------------------------------------------------------------
// Desain pelanggan
// ------------------------------------------------------------

/**
 * Sisi belakang akrilik 2 sisi (KEPUTUSAN 4.10): dokumen sendiri, atau desain yang
 * sama dengan depan (dicetak terbaca dari belakang).
 */
export const contourBackSchema = z.union([
  z.strictObject({ doc: z.unknown() }),
  z.strictObject({ same: z.literal(true) }),
]);
export type ContourBackInput = z.infer<typeof contourBackSchema>;

/**
 * Dokumen editor bebas + isian kontur (bentuk, jarak tepi, lubang). Varian 2 sisi
 * wajib membawa `back`: garis potong dihitung dari gabungan kedua sisi.
 */
export const attachContourDesignSchema = z.strictObject({
  doc: z.unknown(),
  params: z.record(z.string().max(40), z.unknown()).refine((v) => Object.keys(v).length <= 20, {
    message: 'Isian kontur terlalu banyak',
  }),
  back: contourBackSchema.optional(),
});
export type AttachContourDesignInput = z.infer<typeof attachContourDesignSchema>;

/** Pembuatan desain akrilik kontur (KEPUTUSAN 2A.6): isi yang sama ditambah produk dan varian. */
export const createContourDesignSchema = attachContourDesignSchema.extend(designTargetShape);
export type CreateContourDesignInput = z.infer<typeof createContourDesignSchema>;
